import ImageWithFallback from '@/components/common/image-with-fallback'
import { timezoneDateTime } from '@/helpers/datetime'
import { cn } from '@/lib/utils'
import { useMemo } from 'react'

export default function ManageChampionshipEventStats({
  event,
  gridPositions,
  gridPositionsLabel,
  isGridUnlimited,
}) {
  const hasStartDate = Boolean(event.startDateTime)
  const startDate = useMemo(
    () =>
      hasStartDate
        ? timezoneDateTime({
            datetime: event.startDateTime,
            timezone: event.targetTimezone,
            format: false,
          })
        : null,
    [event.startDateTime, event.targetTimezone, hasStartDate],
  )

  const eventHasFinished = useMemo(
    () =>
      event.finishDateTime
        ? new Date() > new Date(event.finishDateTime)
        : false,
    [event.finishDateTime],
  )

  const startDateText = useMemo(
    () =>
      startDate
        ? {
            day: startDate.format('Do'),
            monthYear: startDate.format('MMMM YYYY'),
            time: startDate.format('HH:mm'),
            timezone: startDate.format('z'),
          }
        : {
            day: 'Date required',
            monthYear: 'Schedule required',
            time: 'Not set',
            timezone: event.targetTimezone || '',
          },
    [event.targetTimezone, startDate],
  )

  const eventHasSpace =
    isGridUnlimited || gridPositions - event._count.attendees > 0
  return (
    <div className="grid grid-cols-1 gap-2 md:gap-5 xl:grid-cols-2">
      <div className="relative h-auto min-h-64 w-full">
        <ImageWithFallback
          src={
            event.customTrackImgUrl ??
            event.track?.coverImgUrl ??
            event.championship.headerImgUrl
          }
          alt="Event Track Image"
          fallbackUrl="/img/default/header.webp"
          className="rounded-sm object-cover"
          fill
        />
      </div>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-3 md:gap-5">
        <EventStat
          upperText="Start Date"
          centreText={
            <span className={cn({ 'text-error-500': !hasStartDate })}>
              {startDateText.day}
            </span>
          }
          lowerText={
            <span className={cn({ 'text-error-500': !hasStartDate })}>
              {startDateText.monthYear}
            </span>
          }
        />

        <EventStat
          upperText="Start Time"
          centreText={
            <span className={cn({ 'text-error-500': !hasStartDate })}>
              {startDateText.time}
            </span>
          }
          lowerText={startDateText.timezone}
        />

        <EventStat
          upperText="Drivers"
          centreText={
            <span
              className={cn('', {
                'text-success-500': eventHasSpace && !eventHasFinished,
                'text-error-500': !eventHasSpace && !eventHasFinished,
              })}
            >
              {event._count.attendees} / {gridPositionsLabel}
            </span>
          }
          lowerText="Registered"
        />

        <EventStat
          upperText="Practice"
          centreText={
            <span>
              x{event.sessions.filter((s) => s.type === 'practice').length}
            </span>
          }
          lowerText="Sessions"
        />

        <EventStat
          upperText="Qualifying"
          centreText={
            <span>
              x{event.sessions.filter((s) => s.type === 'qualifying').length}
            </span>
          }
          lowerText="Sessions"
        />

        <EventStat
          upperText="Race"
          centreText={
            <span>
              x{event.sessions.filter((s) => s.type === 'race').length}
            </span>
          }
          lowerText="Sessions"
        />
      </div>
    </div>
  )
}

function EventStat({ upperText, centreText, lowerText }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 text-nowrap rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 p-4 text-xs sm:text-p-2 md:min-h-[196px]">
      <span className="text-warning-50">{upperText}</span>
      <h3 className="text-h-6 font-medium sm:text-h-3-sm">{centreText}</h3>
      <span className="text-wrap text-center text-neutral-blue-500">
        {lowerText}
      </span>
    </div>
  )
}
